import { Statement } from '@angular/compiler';
import { createReducer, on } from '@ngrx/store';
import * as utility from '../../../utility';
import { FlattenGraphQl } from '../../../utility';
import { Cart, ShopifyError } from '../../models';
import * as actions from '../actions/carts.action';

export interface CartsState {
  cart?: Cart;
  cartUpdate?: { merchandiseId: string; quantity: number }[];
  loaded: boolean;
  loading: boolean;
  initializing: boolean;
  initialized: boolean;
  loadingLines: string[];
  error?: ShopifyError[];
  loadingDiscountUpdate: boolean;
  loadedDiscountUpdate: boolean;
  discountUpdateErrors: ShopifyError[] | null;
}

export const initialState: CartsState = {
  cart: undefined,
  cartUpdate: undefined,
  loaded: false,
  loading: false,
  initializing: false,
  initialized: false,
  loadingLines: [],
  loadingDiscountUpdate: false,
  loadedDiscountUpdate: false,
  discountUpdateErrors: [],
};

export const CartsReducer = createReducer(
  initialState,

  on(actions.InitCart, (state: CartsState) => {
    return {
      ...state,
      initializing: true,
      error: [],
    };
  }),
  on(actions.InitCartSuccess, (state: CartsState, { cart }) => {
    return {
      ...state,
      initializing: false,
      initialized: true,
      cart: FlattenGraphQl(cart),
      cartUpdate: undefined,
      error: [],
    };
  }),
  on(actions.InitCartFail, (state: CartsState, { error }) => {
    return {
      ...state,
      initializing: false,
      initialized: true,
      error,
    };
  }),
  on(actions.AddToCartFail, (state: CartsState, { error }) => {
    return {
      ...state,
      loaded: false,
      loading: false,
      error,
    };
  }),
  on(actions.AddToCart, (state: CartsState, { lines }) => {
    return {
      ...state,
      cartUpdate: { ...lines },
      loading: true,
      error: [],
    };
  }),
  on(actions.UpdateDiscounts, (state: CartsState, { discountCodes }) => {
    return {
      ...state,
      loadingDiscountUpdate: true,
      loadedDiscountUpdate: false,
      discountUpdateErrors: [],
    };
  }),
  on(actions.UpdateDiscountsSuccess, (state: CartsState, { cart }) => {
    return {
      ...state,
      loadingDiscountUpdate: false,
      loadedDiscountUpdate: true,
      cart: FlattenGraphQl(cart),
    };
  }),
  on(actions.UpdateDiscountsFail, (state: CartsState, { error }) => {
    return {
      ...state,
      loadingDiscountUpdate: false,
      loadedDiscountUpdate: false,
      discountUpdateErrors: error,
    };
  }),
  on(actions.AddToCartSuccess, (state: CartsState, { cart }) => {
    return {
      ...state,
      cart: FlattenGraphQl(cart),
      loading: false,
      loaded: true,
    };
  }),
  on(actions.UpdateCartLine, actions.DeleteCartLine, (state: CartsState, { lineId }) => {
    return {
      ...state,
      loading: true,
      loadingLines: [...state.loadingLines, lineId],
      error: [],
    };
  }),
  on(actions.UpdateCartLineSuccess, actions.DeleteCartLineSuccess, (state: CartsState, { cart, lineId }) => {
    const index = state.loadingLines.indexOf(lineId);
    const newArr = state.loadingLines.slice(index, 1);

    return {
      ...state,
      cart: FlattenGraphQl(cart),
      loading: false,
      loadingLines: index > -1 && state.loadingLines.length === 1 ? [] : [...newArr],
      error: [],
    };
  }),
  on(actions.UpdateCartBuyerIdentity, (state: CartsState, { token }) => {
    return {
      ...state,
      loading: true,
      error: [],
    };
  }),
  on(actions.UpdateCartBuyerIdentitySuccess, (state: CartsState, { cart }) => {
    const c = {
      ...state.cart,
      ...FlattenGraphQl(cart),
      buyerIdentity: FlattenGraphQl(cart).buyerIdentity,
    };

    return {
      ...state,
      cart: c,
      loading: false,
      error: [],
    };
  }),
  on(actions.UpdateCartNote, (state: CartsState, { note }) => {
    return {
      ...state,
      loading: true,
      error: [],
    };
  }),
  on(actions.UpdateCartNoteSuccess, (state: CartsState, { cart }) => {
    const c = {
      ...state.cart,
      ...FlattenGraphQl(cart),
    };

    return {
      ...state,
      cart: c,
      loading: false,
      error: [],
    };
  }),
  on(actions.UpdateCartBuyerIdentityFail, actions.UpdateCartNoteFail, (state: CartsState, { error }) => {
    return {
      ...state,
      loading: false,
      error,
    };
  }),
  on(actions.ClearCartSuccess, (state: CartsState) => {
    return {
      ...initialState,
      initialized: true,
      error: [],
    };
  })
);

export const getCart = (state: CartsState) => state.cart;
export const getCartLoading = (state: CartsState) => state.loading;
export const getCartLoaded = (state: CartsState) => state.loaded;

export const getCartUpdate = (state: CartsState) => state.cartUpdate;

export const getCartInitialized = (state: CartsState) => state.initialized;
export const getCartInitializing = (state: CartsState) => state.initializing;

export const getCartLinesLoading = (state: CartsState) => state.loadingLines;
export const getCartErrors = (state: CartsState) => state.error || [];

export const getLoadingDiscountUpdate = (state: CartsState) => state.loadingDiscountUpdate;
export const getLoadedDiscountUpdate = (state: CartsState) => state.loadedDiscountUpdate;
export const getDiscountUpdateErrors = (state: CartsState) => state.discountUpdateErrors;
